import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Play, Pause, ChevronRight, ChevronLeft, Volume2, Loader2, 
  SkipBack, SkipForward, Settings, X, Maximize2, Minimize2,
  Dna, Microscope, Leaf, Brain, Heart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Biology icons mapping
const BIOLOGY_ICONS = {
  intro: Dna,
  cell_intro: Microscope,
  cell_membrane: Heart,
  nucleus: Brain,
  dna: Dna,
  mitochondria: Heart,
  protein_synthesis: Dna,
  cell_division: Microscope,
  photosynthesis: Leaf,
  genetics: Dna,
  evolution: Leaf,
  ecology: Leaf,
  summary: Brain
};

/**
 * BiologyPresentation - מצגת ביולוגיה מדברת
 * 12-minute course with Hebrew narration and 3D images
 */
export const BiologyPresentation = ({ onBack }) => {
  const [outline, setOutline] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideData, setSlideData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSlide, setIsLoadingSlide] = useState(false);
  const [error, setError] = useState(null);
  const [autoPlay, setAutoPlay] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [voice, setVoice] = useState('nova');
  const [loadedSlides, setLoadedSlides] = useState({});
  
  const audioRef = useRef(null);
  const containerRef = useRef(null);

  // Load presentation outline
  useEffect(() => {
    const loadOutline = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API}/presentation/biology/outline`);
        if (!response.ok) throw new Error('Failed to load outline');
        const data = await response.json();
        setOutline(data);
        setIsLoading(false);
        
        // Load first slide
        loadSlide(0);
      } catch (err) {
        console.error('Failed to load presentation outline:', err);
        setError('לא ניתן לטעון את המצגת');
        setIsLoading(false);
      }
    };
    loadOutline();
  }, []);

  // Load a specific slide with media
  const loadSlide = useCallback(async (slideIndex) => {
    if (!outline) return;
    
    // Check cache first
    if (loadedSlides[slideIndex]) {
      setSlideData(loadedSlides[slideIndex]);
      return;
    }
    
    const slideId = outline.slides[slideIndex]?.id;
    if (!slideId) return;
    
    setIsLoadingSlide(true);
    try {
      const response = await fetch(`${API}/presentation/biology/slide/${slideId}?voice=${voice}`);
      if (!response.ok) throw new Error('Failed to load slide');
      const data = await response.json();
      
      // Cache the loaded slide
      setLoadedSlides(prev => ({ ...prev, [slideIndex]: data }));
      setSlideData(data);
    } catch (err) {
      console.error('Failed to load slide:', err);
      // Use outline data as fallback
      setSlideData(outline.slides[slideIndex]);
    } finally {
      setIsLoadingSlide(false);
    }
  }, [outline, voice, loadedSlides]);

  // Preload next slide
  useEffect(() => {
    if (outline && currentSlide < outline.slides.length - 1) {
      const nextIndex = currentSlide + 1;
      if (!loadedSlides[nextIndex]) {
        // Preload in background
        const slideId = outline.slides[nextIndex]?.id;
        if (slideId) {
          fetch(`${API}/presentation/biology/slide/${slideId}?voice=${voice}`)
            .then(res => res.json())
            .then(data => {
              setLoadedSlides(prev => ({ ...prev, [nextIndex]: data }));
            })
            .catch(() => {});
        }
      }
    }
  }, [currentSlide, outline, voice, loadedSlides]);

  // Handle slide change
  useEffect(() => {
    if (outline) {
      loadSlide(currentSlide);
    }
  }, [currentSlide, outline, loadSlide]);

  // Play/pause audio
  const togglePlay = () => {
    if (!audioRef.current || !slideData?.audio_base64) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Handle audio end - auto advance
  const handleAudioEnd = () => {
    setIsPlaying(false);
    if (autoPlay && currentSlide < outline?.slides?.length - 1) {
      setTimeout(() => {
        setCurrentSlide(prev => prev + 1);
      }, 1500);
    }
  };

  // Auto-play when new slide loads
  useEffect(() => {
    if (slideData?.audio_base64 && audioRef.current && autoPlay && !isLoadingSlide) {
      audioRef.current.load();
      setTimeout(() => {
        audioRef.current.play().catch(() => {});
      }, 500);
    }
  }, [slideData?.audio_base64, autoPlay, isLoadingSlide]);

  // Navigation
  const nextSlide = () => {
    if (outline && currentSlide < outline.slides.length - 1) {
      setIsPlaying(false);
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setIsPlaying(false);
      setCurrentSlide(prev => prev - 1);
    }
  };

  const goToSlide = (index) => {
    setIsPlaying(false);
    setCurrentSlide(index);
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.key === 'ArrowRight' ? prevSlide() : nextSlide();
      } else if (e.key === ' ') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'f') {
        toggleFullscreen();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, isPlaying, outline]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-900 to-cyan-900 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-6 relative">
            <div className="absolute inset-0 bg-emerald-500/30 rounded-full animate-ping"></div>
            <div className="absolute inset-2 bg-emerald-500/50 rounded-full animate-pulse"></div>
            <Dna className="absolute inset-4 w-16 h-16 text-emerald-400 animate-spin" style={{ animationDuration: '3s' }} />
          </div>
          <h2 className="text-2xl font-black text-white mb-2">טוען קורס ביולוגיה</h2>
          <p className="text-emerald-300">מכין תמונות תלת-מימדיות וקריינות...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="text-6xl mb-4">🔬</div>
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button onClick={onBack} className="bg-slate-700 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-600 transition-colors">
            חזרה
          </button>
        </div>
      </div>
    );
  }

  const slide = slideData || outline?.slides[currentSlide];
  const SlideIcon = BIOLOGY_ICONS[slide?.id] || Dna;
  const progress = outline ? ((currentSlide + 1) / outline.slides.length) * 100 : 0;

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900"
      dir="rtl"
    >
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-xl border-b border-emerald-500/20 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 text-sm font-bold"
            data-testid="back-button"
          >
            <ChevronRight className="w-5 h-5" />
            חזרה
          </button>
          
          <div className="text-center">
            <h1 className="text-lg font-black text-white">קורס ביולוגיה</h1>
            <p className="text-xs text-emerald-400">{outline?.total_duration_minutes} דקות • {outline?.total_slides} שקפים</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-slate-400 hover:text-white transition-colors"
              data-testid="settings-button"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-2 text-slate-400 hover:text-white transition-colors"
              data-testid="fullscreen-button"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            <span className="text-emerald-400 font-bold text-sm">
              {currentSlide + 1}/{outline?.slides?.length}
            </span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="max-w-6xl mx-auto mt-2">
          <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      {/* Settings Panel */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSettings(false)}>
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-white">הגדרות</h3>
              <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-white font-bold">ניגון אוטומטי</span>
                <button
                  onClick={() => setAutoPlay(!autoPlay)}
                  className={`w-12 h-6 rounded-full transition-colors ${autoPlay ? 'bg-emerald-500' : 'bg-slate-600'}`}
                  data-testid="autoplay-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-lg transform transition-transform ${autoPlay ? 'translate-x-1' : 'translate-x-6'}`} />
                </button>
              </div>
              
              <div>
                <span className="text-white font-bold mb-2 block">קול קריינות</span>
                <select
                  value={voice}
                  onChange={(e) => {
                    setVoice(e.target.value);
                    setLoadedSlides({});
                  }}
                  className="w-full bg-slate-700 text-white p-3 rounded-xl border border-slate-600"
                  data-testid="voice-select"
                >
                  <option value="nova">נובה (מומלץ)</option>
                  <option value="alloy">אלוי</option>
                  <option value="echo">אקו</option>
                  <option value="onyx">אוניקס</option>
                  <option value="shimmer">שימר</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-4 sm:p-6">
        {slide && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Image/Visual Section */}
            <div className="order-1 lg:order-2">
              <div className="bg-gradient-to-br from-emerald-900/50 to-teal-900/50 rounded-3xl overflow-hidden border border-emerald-500/20 shadow-2xl aspect-[4/3] relative">
                {isLoadingSlide ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-3" />
                      <p className="text-emerald-300 text-sm">יוצר תמונה תלת-מימדית...</p>
                    </div>
                  </div>
                ) : slideData?.image_base64 ? (
                  <img
                    src={`data:image/png;base64,${slideData.image_base64}`}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                    data-testid="slide-image"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-emerald-800/30 to-teal-800/30">
                    <div className="text-center">
                      <SlideIcon className="w-24 h-24 text-emerald-400/50 mx-auto mb-4" />
                      <p className="text-emerald-300/50 text-sm">תמונה בטעינה...</p>
                    </div>
                  </div>
                )}
                
                {/* Slide title overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center gap-2">
                    <SlideIcon className="w-5 h-5 text-emerald-400" />
                    <span className="text-emerald-400 text-sm font-bold">{slide.subtitle}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="order-2 lg:order-1 space-y-6">
              {/* Title */}
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/10">
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-3" data-testid="slide-title">
                  {slide.title}
                </h2>
                <p className="text-lg text-emerald-300 font-medium">
                  {slide.content}
                </p>
              </div>

              {/* Bullet Points */}
              {slide.bullet_points?.length > 0 && (
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
                  <h3 className="text-sm font-bold text-emerald-400 mb-4 uppercase tracking-wider">נקודות עיקריות</h3>
                  <ul className="space-y-3">
                    {slide.bullet_points.map((point, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-white"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <span className="w-7 h-7 bg-emerald-500/20 border border-emerald-500/40 rounded-lg flex items-center justify-center flex-shrink-0 text-emerald-400 font-bold text-sm">
                          {index + 1}
                        </span>
                        <span className="text-base sm:text-lg leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Audio Player */}
              <div className="bg-gradient-to-r from-emerald-600/20 to-teal-600/20 backdrop-blur-xl rounded-3xl p-6 border border-emerald-500/30">
                <div className="flex items-center gap-4">
                  {/* Main play button */}
                  <button
                    onClick={togglePlay}
                    disabled={!slideData?.audio_base64}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all shadow-lg ${
                      slideData?.audio_base64
                        ? isPlaying
                          ? 'bg-emerald-500 hover:bg-emerald-400'
                          : 'bg-gradient-to-br from-emerald-500 to-teal-500 hover:shadow-emerald-500/30 hover:shadow-xl'
                        : 'bg-slate-700 cursor-not-allowed'
                    }`}
                    data-testid="play-button"
                  >
                    {isLoadingSlide ? (
                      <Loader2 className="w-7 h-7 text-white animate-spin" />
                    ) : isPlaying ? (
                      <Pause className="w-7 h-7 text-white" />
                    ) : (
                      <Play className="w-7 h-7 text-white mr-[-3px]" />
                    )}
                  </button>

                  {/* Info */}
                  <div className="flex-1">
                    <p className="text-white font-bold">קריינות AI בעברית</p>
                    <p className="text-emerald-300 text-sm">
                      {isLoadingSlide ? 'טוען אודיו...' : slideData?.audio_base64 ? 'לחץ להשמעה' : 'ממתין...'}
                    </p>
                  </div>

                  {/* Volume indicator */}
                  <div className="flex items-center gap-1 text-emerald-400">
                    <Volume2 className="w-5 h-5" />
                  </div>
                </div>

                {/* Hidden audio element */}
                {slideData?.audio_base64 && (
                  <audio
                    ref={audioRef}
                    src={`data:audio/mp3;base64,${slideData.audio_base64}`}
                    onEnded={handleAudioEnd}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Navigation Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-black/50 backdrop-blur-xl border-t border-emerald-500/20 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Slide thumbnails */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-3 scrollbar-hide">
            {outline?.slides?.map((s, index) => {
              const Icon = BIOLOGY_ICONS[s.id] || Dna;
              return (
                <button
                  key={s.id}
                  onClick={() => goToSlide(index)}
                  className={`flex-shrink-0 p-2 rounded-xl transition-all ${
                    index === currentSlide
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                  title={s.title}
                  data-testid={`slide-nav-${index}`}
                >
                  <Icon className="w-5 h-5" />
                </button>
              );
            })}
          </div>

          {/* Main navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition-all ${
                currentSlide === 0
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-white hover:bg-white/10'
              }`}
              data-testid="prev-button"
            >
              <SkipForward className="w-5 h-5" />
              הקודם
            </button>

            {/* Center info */}
            <div className="text-center">
              <p className="text-white font-bold text-sm">{slide?.title}</p>
              <p className="text-emerald-400 text-xs">
                ~{Math.round(slide?.duration_seconds / 60) || 1} דקות
              </p>
            </div>

            <button
              onClick={nextSlide}
              disabled={currentSlide === (outline?.slides?.length || 1) - 1}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition-all ${
                currentSlide === (outline?.slides?.length || 1) - 1
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'bg-emerald-500 text-white hover:bg-emerald-400'
              }`}
              data-testid="next-button"
            >
              הבא
              <SkipBack className="w-5 h-5" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BiologyPresentation;
